import * as XLSX from "xlsx";
import { normalizarTexto, extraerFecha, convertirDireccion } from "./importar_cartera";
import { VIVIENDAS_PRECARGADAS } from "./pase_visitante";
import { actualizarMultiple } from "./crud";

// Piezas comunes de las cargas masivas desde Excel o CSV (encomiendas, visitantes):
// plantilla, lectura del archivo, títulos de columnas, fechas, horas y direcciones.

export { normalizarTexto };

// ---------- Plantilla y exportación ----------

// Libro con la hoja de datos (títulos + filas de ejemplo) y una hoja de instrucciones.
export const descargarPlantilla = ({ archivo, hoja, columnas, ejemplos, anchos, instrucciones }) => {
  const datos = XLSX.utils.aoa_to_sheet([columnas, ...ejemplos]);
  datos["!cols"] = anchos.map(wch => ({ wch }));
  const ayuda = XLSX.utils.aoa_to_sheet([
    ["CÓMO LLENAR LA PLANTILLA"],
    [""],
    [`1. Escriba un registro por fila, desde la fila 2 de la hoja ${hoja}.`],
    ["2. Borre las filas de ejemplo antes de cargar el archivo."],
    ["3. No cambie los títulos de la primera fila."],
    [""],
    ["COLUMNA", "¿ES OBLIGATORIA?", "CÓMO ESCRIBIRLA"],
    ...instrucciones,
  ]);
  ayuda["!cols"] = [{ wch: 20 }, { wch: 22 }, { wch: 100 }];
  const libro = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(libro, datos, hoja);
  XLSX.utils.book_append_sheet(libro, ayuda, "Instrucciones");
  XLSX.writeFile(libro, archivo);
};

// CSV separado por ";" y con BOM para que Excel lea las tildes (igual que el antiguo botón de exportar).
export const exportarCSV = (columnas, filas, archivo) => {
  const hoja = XLSX.utils.aoa_to_sheet([
    columnas.map(c => c.label),
    ...filas.map(f => columnas.map(c => f[c.key] ?? "")),
  ]);
  const csv = "﻿" + XLSX.utils.sheet_to_csv(hoja, { FS: ";" });
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const enlace = document.createElement("a");
  enlace.href = url;
  enlace.download = archivo;
  enlace.click();
  URL.revokeObjectURL(url);
};

// ---------- Lectura ----------

const decodificarTexto = (buffer) => {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(buffer);
  } catch {
    return new TextDecoder("windows-1252").decode(buffer);
  }
};

export const leerLibro = async (archivo) => {
  const buffer = await archivo.arrayBuffer();
  const esTexto = /\.(csv|txt|tsv)$/i.test(archivo.name);
  return esTexto
    ? XLSX.read(decodificarTexto(buffer), { type: "string", raw: true })
    : XLSX.read(buffer, { type: "array" });
};

// Busca en las hojas (menos la de instrucciones) la fila de títulos.
// reglas: [[campo, regex sobre el título normalizado], ...]; la primera que coincide decide.
// esEncabezado(columnas) dice si los campos encontrados bastan.
// Devuelve { filas, indice, columnas } o null.
export const buscarTabla = (libro, reglas, esEncabezado) => {
  for (const nombreHoja of libro.SheetNames) {
    if (normalizarTexto(nombreHoja) === "instrucciones") continue;
    const filas = XLSX.utils.sheet_to_json(libro.Sheets[nombreHoja], { header: 1, defval: "", blankrows: true, raw: true });
    for (let i = 0; i < Math.min(filas.length, 20); i++) {
      const columnas = {};
      filas[i].forEach((celda, j) => {
        const t = normalizarTexto(celda);
        const regla = t && reglas.find(([, re]) => re.test(t));
        if (regla && columnas[regla[0]] === undefined) columnas[regla[0]] = j;
      });
      if (esEncabezado(columnas)) return { filas, indice: i, columnas };
    }
  }
  return null;
};

// ---------- Valores ----------

export const vacio = (v) => String(v ?? "").trim() === "";
export const texto = (v) => String(v ?? "").replace(/\s+/g, " ").trim();
export const sinDato = (v) => vacio(v) || /^(n\/?a|null|ninguno|-)$/i.test(String(v).trim());

// Busca el valor en la lista sin importar mayúsculas ni tildes. Devuelve el valor de la lista o undefined.
export const elegirDeLista = (valor, lista) => lista.find(v => normalizarTexto(v) === normalizarTexto(valor));

const PALABRAS_DIRECCION = /\b(TORRE|APARTAMENTO|APTO|APT|AP|MANZANA|MZ|MZA|CASA|CS|LOCAL|LC|BLOQUE|BL|INTERIOR|INT|ETAPA)\b/;

// Las viviendas especiales (ADMINISTRACIÓN, PORTERIA...) se dejan tal cual; las demás
// se llevan al formato de CoordiPH: "MZ C CS 2" queda "MANZANA C CASA 2".
export const normalizarDireccion = (valor) => {
  const t = String(valor ?? "").replace(/\s+/g, " ").trim().toUpperCase();
  if (!t) return "";
  const especial = VIVIENDAS_PRECARGADAS.find(v => normalizarTexto(v) === normalizarTexto(t));
  if (especial) return especial;
  const sinTildes = t.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/([A-Z])(\d)/g, "$1 $2");
  return PALABRAS_DIRECCION.test(sinTildes) ? convertirDireccion(t) : t;
};

const pad = (n) => String(n).padStart(2, "0");

export const momentoDe = (fecha = new Date()) => ({
  fecha: `${fecha.getFullYear()}-${pad(fecha.getMonth() + 1)}-${pad(fecha.getDate())}`,
  hora: `${pad(fecha.getHours())}:${pad(fecha.getMinutes())}`,
});

const horaDesde = (h, min, ampm) => {
  let hora = Number(h);
  const minutos = Number(min);
  if (ampm) {
    if (hora < 1 || hora > 12) return null;
    hora = ampm.startsWith("p") ? (hora % 12) + 12 : hora % 12;
  }
  if (hora > 23 || minutos > 59) return null;
  return `${pad(hora)}:${pad(minutos)}`;
};

const AMPM = "(a\\.?\\s*m\\.?|p\\.?\\s*m\\.?)";

// Devuelve "HH:mm" o null. Acepta "14:15", "2:15 pm", "14.15" y horas de Excel (fracción del día).
export const extraerHora = (valor) => {
  if (typeof valor === "number") {
    const minutos = Math.round((valor - Math.floor(valor)) * 24 * 60) % (24 * 60);
    return `${pad(Math.floor(minutos / 60))}:${pad(minutos % 60)}`;
  }
  const m = String(valor ?? "").trim().toLowerCase().match(new RegExp(`^(\\d{1,2})[:.](\\d{2})(?:[:.]\\d{2})?\\s*${AMPM}?$`));
  return m ? horaDesde(m[1], m[2], m[3]) : null;
};

// Lee una celda con fecha y, si la trae, hora: "07/10/2026 09:38", "2026-10-07 09:38 AM"
// (como sale de un listado exportado) o una fecha de Excel. Devuelve { fecha, hora } o null.
export const extraerFechaHora = (valor) => {
  const fecha = extraerFecha(valor, true);
  if (!fecha) return null;
  if (typeof valor === "number") return { fecha, hora: valor % 1 !== 0 ? extraerHora(valor) : null };
  const m = String(valor).toLowerCase().match(new RegExp(`(?:^|\\s|t)(\\d{1,2}):(\\d{2})(?::\\d{2}(?:\\.\\d+)?)?\\s*${AMPM}?`));
  if (!m) return { fecha, hora: null };
  const hora = horaDesde(m[1], m[2], m[3]);
  return hora ? { fecha, hora } : null;
};

// Lee la fecha (y la hora, en la misma celda o en otra columna) de una fila.
// Devuelve { fecha, hora } (hora puede ser null), o null si está vacía o tiene error (el error se agrega a problemas).
export const leerMomento = (valorFecha, valorHora, nombre, problemas) => {
  if (vacio(valorFecha)) {
    if (!vacio(valorHora)) problemas.push(`escribió la hora ${nombre} pero falta el día`);
    return null;
  }
  const leida = extraerFechaHora(valorFecha);
  if (!leida) {
    problemas.push(`la fecha ${nombre} "${valorFecha}" no se entiende; escríbala como 07/10/2026 09:38`);
    return null;
  }
  if (vacio(valorHora)) return leida;
  const hora = extraerHora(valorHora);
  if (!hora) {
    problemas.push(`la hora ${nombre} "${valorHora}" no se entiende; escríbala como 14:15`);
    return null;
  }
  return { fecha: leida.fecha, hora };
};

// Hasta 4 direcciones distintas para mostrar cómo quedaron.
export const ejemplosDirecciones = (registros) => {
  const ejemplos = [];
  for (const r of registros) {
    if (ejemplos.length >= 4) break;
    if (!ejemplos.some(e => e.direccion === r.vivienda)) ejemplos.push({ original: r.original, direccion: r.vivienda });
  }
  return ejemplos;
};

// Date local de un { fecha: "aaaa-mm-dd", hora: "HH:mm" }. Los milisegundos separan
// registros del mismo minuto para que no queden con la misma fecha de creación.
export const fechaDeMomento = ({ fecha, hora }, milisegundos = 0) => {
  const [a, m, d] = fecha.split("-").map(Number);
  const [h, mi] = hora.split(":").map(Number);
  return new Date(a, m - 1, d, h, mi, 0, milisegundos % 1000);
};

// Guarda todos los registros en una sola operación: si algo falla, no queda nada a medias.
// armar(registro, indice, contexto) devuelve { ruta: valor, ... }; preparar(nit) puede traer
// datos extra para el contexto. Devuelve { exito, mensaje }.
export const guardarCambiosMasivos = async (registros, armar, preparar = async () => ({})) => {
  try {
    const nit = localStorage.getItem("CodigoCliente");
    if (!nit) throw new Error("No se encontró el código del conjunto. Cierre sesión y vuelva a entrar.");
    const contexto = {
      nit,
      usuario: localStorage.getItem("NombreCompleto") || "",
      ahora: new Date(),
      ...(await preparar(nit)),
    };
    const cambios = {};
    registros.forEach((registro, i) => Object.assign(cambios, armar(registro, i, contexto)));
    await actualizarMultiple(cambios);
    return { exito: true };
  } catch (error) {
    console.error("Error en la carga masiva:", error);
    return { exito: false, mensaje: error.message };
  }
};
